import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { vt } from '../theme';

const useStyles = makeStyles({
  card: {
    position: 'relative',
    minWidth: 0,
    height: '84px',
    boxSizing: 'border-box',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: vt.surface3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  label: { fontSize: '11px', letterSpacing: '0.3px', color: vt.fg3 },
  value: {
    fontSize: '20px',
    lineHeight: '26px',
    fontWeight: tokens.fontWeightSemibold,
    color: vt.fg1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  mono: { fontFamily: tokens.fontFamilyMonospace },
  sub: { fontSize: '11px', color: vt.fg3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  dot: { position: 'absolute', top: '16px', right: '16px', width: '8px', height: '8px', borderRadius: '50%' },
  right: { position: 'absolute', right: '16px', bottom: '14px' },
});

export interface StatCardProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  /** Numbers that are measurements (rates, money) read in mono, like the mockup. */
  mono?: boolean;
  /** A state dot in the top-right corner, as a CSS colour. */
  dot?: string;
  /** Anything for the bottom-right corner, such as the Throughput card's sparkline. */
  right?: ReactNode;
}

/** One tile of a stat strip: a single number you can read from across the room, with its context below. */
export function StatCard({ label, value, sub, mono, dot, right }: StatCardProps) {
  const s = useStyles();
  return (
    <section className={s.card} aria-label={label}>
      <span className={s.label}>{label}</span>
      <span className={mergeClasses(s.value, mono && s.mono)}>{value}</span>
      <span className={s.sub}>{sub}</span>
      {dot && <span className={s.dot} style={{ backgroundColor: dot }} aria-hidden />}
      {right && <span className={s.right}>{right}</span>}
    </section>
  );
}
